import { useEffect, useRef } from 'react';

interface TicketTier {
  id: string;
  name: string;
  price: number;
  subtitle: string;
  benefits: string[];
  extraBenefits?: { label: string; items: string[] };
  highlighted: boolean;
  checkoutUrl: string;
  installment: string;
}

const tickets: TicketTier[] = [
  {
    id: 'future_pass',
    name: 'Future Pass',
    price: 179,
    subtitle: 'Seu acesso completo ao The Future Wedding',
    benefits: [
      'Acesso aos 2 dias de imersão',
      'Participação em todos os Talks e Painéis',
      'Acesso ao espaço Curadoria & Conexões',
      'Welcome Brunch de recepção',
      'Credenciamento exclusivo',
      'Goodie Bag oficial do evento',
      'Cartão de programação',
      'Experiência Noiva Lab',
      'Live Wedding',
      'Experiências gastronômicas durante a programação',
      'Networking com fornecedores e especialistas',
    ],
    highlighted: false,
    checkoutUrl: 'https://pay.kiwify.com.br/HHaR6ph',
    installment: 'ou em até 10x de R$21,51',
  },
  {
    id: 'future_experience',
    name: 'Future Experience',
    price: 269,
    subtitle: 'A forma mais exclusiva de viver o The Future Wedding',
    benefits: [
      'Acesso aos 2 dias de imersão',
      'Participação em todos os Talks e Painéis',
      'Acesso ao espaço Curadoria & Conexões',
      'Welcome Brunch de recepção',
      'Credenciamento exclusivo',
      'Goodie Bag oficial do evento',
      'Cartão de programação',
      'Experiência Noiva Lab',
      'Live Wedding',
      'Experiências gastronômicas durante a programação',
      'Networking com fornecedores e especialistas',
    ],
    extraBenefits: {
      label: 'Benefícios Exclusivos',
      items: [
        'Jantar Exclusivo de Encerramento — Experiência gastronômica intimista ao final do segundo dia, ao lado dos anfitriões, palestrantes e parceiros do evento',
        'Convite para 1 acompanhante — Seu noivo(a) poderá participar desse momento especial',
        'Mesa reservada durante toda a programação',
        'Experiência exclusiva de networking',
      ],
    },
    highlighted: true,
    checkoutUrl: 'https://pay.kiwify.com.br/YmGyDFd',
    installment: 'ou em até 10x de R$32,33',
  },
];

export default function InfoAccessSection() {
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.querySelectorAll('.reveal-on-scroll').forEach((el) => {
              el.classList.add('revealed');
            });
          }
        });
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <section ref={sectionRef} id="informacoes" className="relative py-24 md:py-36 px-6 md:px-10" style={{ backgroundColor: 'oklch(var(--background-100))' }}>
      <div className="max-w-6xl mx-auto">
        {/* Header: title + badge */}
        <div className="reveal-on-scroll flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-16 md:mb-20">
          <h2
            className="font-heading text-4xl md:text-5xl lg:text-6xl leading-[1.1]"
            style={{ color: 'oklch(var(--foreground-900))' }}
          >
            Informações e
            <br />
            Acesso
          </h2>
          <span
            className="inline-block self-start md:self-auto text-[10px] tracking-[0.25em] uppercase px-4 py-2 rounded-full border"
            style={{
              color: 'oklch(var(--secondary-500))',
              borderColor: 'oklch(var(--secondary-300) / 0.5)',
            }}
          >
            Vagas Limitadas
          </span>
        </div>

        {/* Main grid: left info card + right ticket cards */}
        <div className="reveal-on-scroll grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* LEFT — Info Card (Local + Datas) */}
          <div
            className="rounded-2xl p-8 md:p-10 space-y-10"
            style={{ backgroundColor: 'oklch(var(--background-50))' }}
          >
            {/* Local */}
            <div className="flex items-start gap-5">
              <div className="flex-shrink-0 w-10 h-10 flex items-center justify-center">
                <i className="ri-map-pin-2-line text-xl" style={{ color: 'oklch(var(--secondary-500))' }}></i>
              </div>
              <div>
                <p className="text-[10px] tracking-[0.2em] uppercase mb-1" style={{ color: 'oklch(var(--foreground-500))' }}>
                  Local
                </p>
                <p className="font-heading text-xl md:text-2xl" style={{ color: 'oklch(var(--foreground-900))' }}>
                  Casa Don Tito
                </p>
                <p className="text-sm mt-0.5" style={{ color: 'oklch(var(--foreground-500))' }}>
                  Ribeirão Preto
                </p>
              </div>
            </div>

            {/* Datas e Horários */}
            <div className="flex items-start gap-5">
              <div className="flex-shrink-0 w-10 h-10 flex items-center justify-center">
                <i className="ri-calendar-line text-xl" style={{ color: 'oklch(var(--secondary-500))' }}></i>
              </div>
              <div className="flex-1">
                <p className="text-[10px] tracking-[0.2em] uppercase mb-3" style={{ color: 'oklch(var(--foreground-500))' }}>
                  Datas e Horários
                </p>

                <div className="space-y-4">
                  {/* Dia 16 */}
                  <div className="flex items-start gap-3">
                    <div className="w-px self-stretch rounded-full mt-1 mb-1" style={{ backgroundColor: 'oklch(var(--secondary-300))' }}></div>
                    <div>
                      <p className="text-sm font-semibold" style={{ color: 'oklch(var(--foreground-900))' }}>
                        16 de Julho
                      </p>
                      <p className="text-sm flex items-center gap-1.5 mt-0.5" style={{ color: 'oklch(var(--foreground-600))' }}>
                        <i className="ri-time-line text-xs" style={{ color: 'oklch(var(--secondary-400))' }}></i>
                        Início às 18h
                      </p>
                    </div>
                  </div>

                  {/* Dia 17 */}
                  <div className="flex items-start gap-3">
                    <div className="w-px self-stretch rounded-full mt-1 mb-1" style={{ backgroundColor: 'oklch(var(--secondary-300))' }}></div>
                    <div>
                      <p className="text-sm font-semibold" style={{ color: 'oklch(var(--foreground-900))' }}>
                        17 de Julho
                      </p>
                      <div className="space-y-1 mt-0.5">
                        <p className="text-sm flex items-center gap-1.5" style={{ color: 'oklch(var(--foreground-600))' }}>
                          <i className="ri-time-line text-xs" style={{ color: 'oklch(var(--secondary-400))' }}></i>
                          Curadoria e Conexões das 14h às 17h
                        </p>
                        <p className="text-sm flex items-center gap-1.5" style={{ color: 'oklch(var(--foreground-600))' }}>
                          <i className="ri-time-line text-xs" style={{ color: 'oklch(var(--secondary-400))' }}></i>
                          Imersão a partir das 18h
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* RIGHT — Two ticket cards side by side (stacked on mobile) */}
          <div className="flex flex-col md:flex-row gap-4">
            {tickets.map((ticket) => (
              <div
                key={ticket.id}
                className="flex-1 rounded-2xl p-6 md:p-8 flex flex-col relative"
                style={{ backgroundColor: 'oklch(var(--background-50))' }}
              >
                {/* Highlight badge */}
                {ticket.highlighted && (
                  <span
                    className="absolute -top-3 left-1/2 -translate-x-1/2 text-[10px] tracking-[0.15em] uppercase px-4 py-1 rounded-full font-semibold whitespace-nowrap"
                    style={{ backgroundColor: '#ADC178', color: '#FFFFFF' }}
                  >
                    Mais Exclusivo
                  </span>
                )}

                {/* Name & Subtitle */}
                <div className="mb-1">
                  <h3 className="font-heading text-xl md:text-2xl mb-2" style={{ color: 'oklch(var(--foreground-900))' }}>
                    {ticket.name}
                  </h3>
                  <p className="text-xs leading-relaxed" style={{ color: 'oklch(var(--foreground-500))' }}>
                    {ticket.subtitle}
                  </p>
                </div>

                {/* Price */}
                <div className="mt-5 mb-1">
                  <span className="text-sm font-medium" style={{ color: 'oklch(var(--foreground-500))' }}>R$ </span>
                  <span className="font-heading text-4xl md:text-5xl" style={{ color: 'oklch(var(--foreground-900))' }}>{ticket.price}</span>
                  <span className="text-sm font-medium" style={{ color: 'oklch(var(--foreground-500))' }}>,00</span>
                </div>
                <p className="text-xs mb-1" style={{ color: 'oklch(var(--foreground-400))' }}>
                  pagamento único
                </p>
                <p className="text-xs mb-5" style={{ color: 'oklch(var(--foreground-500))' }}>
                  {ticket.installment}
                </p>

                {/* Divider */}
                <div className="divider-accent mb-5"></div>

                {/* Benefits */}
                <div className="flex-1 space-y-0 mb-6">
                  <p className="text-[10px] tracking-[0.2em] uppercase font-semibold mb-3" style={{ color: 'oklch(var(--foreground-700))' }}>
                    O ingresso inclui:
                  </p>
                  <ul className="space-y-2">
                    {ticket.benefits.map((benefit, i) => (
                      <li key={i} className="flex items-start gap-2.5">
                        <span className="flex-shrink-0 w-4 h-4 rounded-full flex items-center justify-center mt-0.5" style={{ backgroundColor: 'oklch(var(--primary-100))' }}>
                          <i className="ri-check-line text-[8px]" style={{ color: 'oklch(var(--primary-500))' }}></i>
                        </span>
                        <span className="text-xs leading-snug" style={{ color: 'oklch(var(--foreground-600))' }}>
                          {benefit}
                        </span>
                      </li>
                    ))}
                  </ul>

                  {/* Extra benefits */}
                  {ticket.extraBenefits && (
                    <div className="mt-4 pt-4" style={{ borderTop: '1px solid oklch(var(--accent-200) / 0.6)' }}>
                      <p className="text-[10px] tracking-[0.2em] uppercase font-semibold mb-3" style={{ color: 'oklch(var(--accent-600))' }}>
                        {ticket.extraBenefits.label}
                      </p>
                      <ul className="space-y-2">
                        {ticket.extraBenefits.items.map((item, i) => (
                          <li key={i} className="flex items-start gap-2.5">
                            <span className="flex-shrink-0 w-4 h-4 rounded-full flex items-center justify-center mt-0.5" style={{ backgroundColor: 'oklch(var(--accent-100))' }}>
                              <i className="ri-star-fill text-[8px]" style={{ color: 'oklch(var(--accent-500))' }}></i>
                            </span>
                            <span className="text-xs leading-snug" style={{ color: 'oklch(var(--foreground-600))' }}>
                              {item}
                            </span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>

                {/* CTA */}
                <a
                  href={ticket.checkoutUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-full py-3 text-xs tracking-[0.15em] uppercase rounded-md transition-all duration-300 whitespace-nowrap cursor-pointer font-semibold hover:opacity-90 flex items-center justify-center gap-2 text-center"
                  style={{
                    backgroundColor: ticket.highlighted ? '#ADC178' : 'oklch(var(--foreground-900))',
                    color: '#FFFFFF',
                  }}
                >
                  Garantir Minha Vaga
                  <i className="ri-arrow-right-line text-sm"></i>
                </a>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}